import { Compass, MessageCircle, Heart, User } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/useTranslation";

interface BottomNavigationProps {
  currentPage: 'explore' | 'chat' | 'myBoyfriends' | 'myProfile';
  onNavigateToExplore: () => void;
  onNavigateToChat: () => void;
  onNavigateToMyBoyfriends: () => void;
  onNavigateToProfile: () => void;
  onNavigateToPremium?: () => void;
  user?: {
    email?: string;
    name?: string;
    isPremium?: boolean;
  } | null;
  onPromptLogin?: () => void;
  unreadMessagesCount?: number;
}

export const BottomNavigation = ({
  currentPage,
  onNavigateToExplore,
  onNavigateToChat,
  onNavigateToMyBoyfriends,
  onNavigateToProfile,
  onNavigateToPremium,
  user,
  onPromptLogin,
  unreadMessagesCount = 0
}: BottomNavigationProps) => {
  const { t } = useTranslation();
  
  const navItems = [
    {
      id: 'explore',
      title: t('navigation.explore'),
      icon: Compass,
      action: onNavigateToExplore,
      active: currentPage === 'explore',
    },
    {
      id: 'chat',
      title: t('navigation.messages'),
      icon: MessageCircle,
      action: onNavigateToChat,
      active: currentPage === 'chat',
    },
    {
      id: 'myBoyfriends',
      title: t('navigation.favorites'),
      icon: Heart,
      action: onNavigateToMyBoyfriends,
      active: currentPage === 'myBoyfriends',
    },
    {
      id: 'myProfile',
      title: t('navigation.my_account'),
      icon: User,
      action: () => user ? onNavigateToProfile() : onPromptLogin?.(),
      active: currentPage === 'myProfile',
    },
  ];

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-background border-t border-border z-50" style={{ paddingBottom: 'var(--safe-area-inset-bottom, 0px)' }}>
      <div className="flex items-center justify-around py-2 px-4 max-w-md mx-auto">
        {navItems.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              onClick={() => {
                console.log(`Navigation button clicked: ${item.id}`);
                if (item.action) {
                  item.action();
                } else {
                  console.error(`No action defined for ${item.id}`);
                }
              }}
              className={cn(
                "flex flex-col items-center gap-1 py-2 px-3 rounded-lg transition-all duration-200 relative",
                item.active
                  ? "text-primary font-medium bg-primary/10" 
                  : "text-muted-foreground"
              )}
            >
              <div className="relative">
                <Icon className={cn("h-5 w-5", item.active && "text-primary")} />
                {item.id === 'chat' && unreadMessagesCount > 0 && (
                  <div className="absolute -top-1 -right-1 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full h-3 w-3" />
                )}
              </div>
              <span className="text-xs font-medium">{item.title}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};
